import { Link } from "react-router-dom";
import { Calendar, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Calendar className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                FacilePrenotare
              </span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Torna alla Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Termini e Condizioni di Servizio
          </h1>
          <p className="text-slate-500 mb-8">Ultimo aggiornamento: Gennaio 2026</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Accettazione dei Termini</h2>
              <p className="text-slate-600 leading-relaxed">
                Utilizzando la piattaforma FacilePrenotare ("Servizio"), accetti di essere vincolato dai presenti 
                Termini e Condizioni di Servizio ("Termini"). Se non accetti questi Termini, ti preghiamo di 
                non utilizzare il Servizio. L'utilizzo del Servizio implica l'accettazione integrale dei 
                presenti Termini e dell'Informativa sulla Privacy.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Descrizione del Servizio</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                FacilePrenotare è una piattaforma SaaS (Software as a Service) che permette a professionisti, 
                studi e attività commerciali di:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Gestire calendari e disponibilità per appuntamenti</li>
                <li>Ricevere prenotazioni online dai propri clienti</li>
                <li>Gestire servizi, staff e risorse</li>
                <li>Accettare pagamenti online tramite Stripe</li>
                <li>Sincronizzare appuntamenti con Google Calendar</li>
                <li>Inviare notifiche e promemoria automatici</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Registrazione e Account</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">3.1 Requisiti</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Per utilizzare il Servizio devi: (a) avere almeno 18 anni; (b) fornire informazioni accurate 
                    e complete durante la registrazione; (c) mantenere aggiornate le informazioni del tuo account.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">3.2 Sicurezza dell'Account</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Sei responsabile della riservatezza delle credenziali di accesso e di tutte le attività 
                    svolte tramite il tuo account. Devi notificarci immediatamente qualsiasi uso non autorizzato.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">3.3 Un Account per Utente</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Ogni utente può avere un solo account. La creazione di account multipli può comportare 
                    la sospensione o cancellazione degli stessi.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Piani e Prezzi</h2>
              <div className="bg-slate-50 rounded-lg p-6 mb-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
                    <h3 className="font-semibold text-slate-900">Starter</h3>
                    <p className="text-2xl font-bold text-slate-900 mt-2">Gratis</p>
                    <p className="text-sm text-slate-500 mt-1">1 calendario, 50 prenotazioni/mese</p>
                  </div>
                  <div className="text-center p-4 bg-primary text-white rounded-lg">
                    <h3 className="font-semibold">Professional</h3>
                    <p className="text-2xl font-bold mt-2">€29/mese</p>
                    <p className="text-sm opacity-80 mt-1">Illimitato, Stripe, Google Calendar</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
                    <h3 className="font-semibold text-slate-900">Business</h3>
                    <p className="text-2xl font-bold text-slate-900 mt-2">€79/mese</p>
                    <p className="text-sm text-slate-500 mt-1">Staff illimitato, multi-sede, API</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">4.1 Fatturazione</h3>
                  <p className="text-slate-600 leading-relaxed">
                    I piani a pagamento sono fatturati mensilmente in anticipo. I prezzi sono espressi in Euro 
                    e sono IVA esclusa. La fattura verrà emessa automaticamente e inviata via email.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">4.2 Modifiche ai Prezzi</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Ci riserviamo il diritto di modificare i prezzi con un preavviso di 30 giorni. Le modifiche 
                    non si applicheranno al periodo di fatturazione in corso.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">4.3 Rimborsi</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Non sono previsti rimborsi per periodi parziali o per upgrade/downgrade di piano. In caso 
                    di cancellazione, il servizio rimarrà attivo fino alla fine del periodo pagato.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Utilizzo Accettabile</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Utilizzando il Servizio, accetti di <strong>non</strong>:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Violare leggi o regolamenti applicabili</li>
                <li>Pubblicare contenuti illegali, diffamatori, osceni o offensivi</li>
                <li>Inviare spam o comunicazioni non sollecitate</li>
                <li>Tentare di accedere a sistemi o dati non autorizzati</li>
                <li>Interferire con il funzionamento del Servizio</li>
                <li>Utilizzare il Servizio per attività fraudolente</li>
                <li>Rivendere o sublicenziare il Servizio senza autorizzazione</li>
                <li>Copiare, modificare o decompilare il software</li>
                <li>Raccogliere dati di altri utenti senza consenso</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Contenuti dell'Utente</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">6.1 Proprietà</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Mantieni tutti i diritti sui contenuti che carichi sulla piattaforma (testi, immagini, 
                    informazioni sui servizi, ecc.). Concedi a FacilePrenotare una licenza limitata per 
                    visualizzare e trasmettere tali contenuti nell'ambito del Servizio.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">6.2 Responsabilità</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Sei l'unico responsabile dei contenuti che pubblichi. Garantisci di avere i diritti 
                    necessari per pubblicare tali contenuti e che gli stessi non violano diritti di terzi.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Proprietà Intellettuale</h2>
              <p className="text-slate-600 leading-relaxed">
                FacilePrenotare, inclusi il logo, il design, il software, i testi e le grafiche, sono protetti 
                da diritti d'autore, marchi registrati e altre leggi sulla proprietà intellettuale. Non è 
                consentito copiare, modificare, distribuire o creare opere derivate senza autorizzazione scritta.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Integrazioni di Terze Parti</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Il Servizio si integra con servizi di terze parti:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Stripe:</strong> per l'elaborazione dei pagamenti</li>
                <li><strong>Google Calendar:</strong> per la sincronizzazione degli appuntamenti</li>
                <li><strong>Cloudinary:</strong> per lo storage delle immagini</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                L'utilizzo di questi servizi è soggetto ai rispettivi termini e condizioni. FacilePrenotare 
                non è responsabile per malfunzionamenti o modifiche ai servizi di terze parti.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Limitazione di Responsabilità</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <p className="text-slate-700 leading-relaxed">
                  IL SERVIZIO È FORNITO "COSÌ COM'È" E "COME DISPONIBILE". PRENOTAFACILE NON GARANTISCE 
                  CHE IL SERVIZIO SARÀ ININTERROTTO, SICURO O PRIVO DI ERRORI.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  IN NESSUN CASO PRENOTAFACILE SARÀ RESPONSABILE PER DANNI INDIRETTI, INCIDENTALI, SPECIALI, 
                  CONSEQUENZIALI O PUNITIVI, INCLUSA LA PERDITA DI PROFITTI, DATI, USO O AVVIAMENTO.
                </p>
                <p className="text-slate-700 leading-relaxed mt-4">
                  LA RESPONSABILITÀ MASSIMA DI PRENOTAFACILE È LIMITATA ALL'IMPORTO PAGATO DALL'UTENTE 
                  NEI 12 MESI PRECEDENTI L'EVENTO CHE HA DATO ORIGINE ALLA RICHIESTA.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Indennizzo</h2>
              <p className="text-slate-600 leading-relaxed">
                Accetti di indennizzare e manlevare FacilePrenotare, i suoi dirigenti, dipendenti e partner 
                da qualsiasi reclamo, danno, perdita, responsabilità e spesa (incluse le spese legali) 
                derivanti da: (a) l'utilizzo del Servizio; (b) la violazione di questi Termini; (c) la 
                violazione di diritti di terzi.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Sospensione e Cancellazione</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">11.1 Da Parte Nostra</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Ci riserviamo il diritto di sospendere o cancellare il tuo account in caso di: violazione 
                    dei Termini, mancato pagamento, attività fraudolente o illegali, o su richiesta delle autorità.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">11.2 Da Parte Tua</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Puoi cancellare il tuo account in qualsiasi momento dalle impostazioni. La cancellazione 
                    sarà effettiva alla fine del periodo di fatturazione corrente. I tuoi dati saranno 
                    eliminati entro 30 giorni dalla cancellazione.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">11.3 Effetti della Cancellazione</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Alla cancellazione, perderai l'accesso al Servizio e a tutti i dati associati. Ti 
                    consigliamo di esportare i tuoi dati prima della cancellazione.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Modifiche ai Termini</h2>
              <p className="text-slate-600 leading-relaxed">
                Ci riserviamo il diritto di modificare questi Termini in qualsiasi momento. Le modifiche 
                saranno comunicate via email e pubblicate su questa pagina almeno 30 giorni prima 
                dell'entrata in vigore. L'utilizzo continuato del Servizio dopo l'entrata in vigore 
                delle modifiche costituisce accettazione dei nuovi Termini.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">13. Legge Applicabile e Foro Competente</h2>
              <p className="text-slate-600 leading-relaxed">
                I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia derivante 
                dall'interpretazione o esecuzione dei presenti Termini, sarà competente in via esclusiva 
                il Foro di Milano, salvo diversa disposizione inderogabile di legge a favore del consumatore.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">14. Disposizioni Generali</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Intero Accordo:</strong> Questi Termini costituiscono l'intero accordo tra te e FacilePrenotare</li>
                <li><strong>Separabilità:</strong> L'invalidità di una clausola non pregiudica le altre</li>
                <li><strong>Rinuncia:</strong> La mancata applicazione di un diritto non costituisce rinuncia</li>
                <li><strong>Cessione:</strong> Non puoi cedere i tuoi diritti senza il nostro consenso scritto</li>
                <li><strong>Forza Maggiore:</strong> Non siamo responsabili per inadempimenti dovuti a cause di forza maggiore</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">15. Contatti</h2>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <p className="text-slate-600 leading-relaxed">
                  Per qualsiasi domanda relativa a questi Termini:
                </p>
                <ul className="mt-4 space-y-2 text-slate-600">
                  <li>
                    <strong>Email:</strong>{" "}
                    <a href="mailto:info@appstream.it" className="text-primary hover:underline">
                      info@appstream.it
                    </a>
                  </li>
                  <li>
                    <strong>Supporto:</strong>{" "}
                    <a href="mailto:info@appstream.it" className="text-primary hover:underline">
                      info@appstream.it
                    </a>
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" strokeWidth={1.5} />
              <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                FacilePrenotare
              </span>
            </Link>
            <div className="flex gap-6 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-slate-900">Termini di Servizio</Link>
            </div>
            <p className="text-sm text-slate-500">
              © 2026 FacilePrenotare. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
